/**
 * reviewApi — typed client for the post-order review BFF routes
 * (Block 7 M1, Task 7).
 *
 * Reviews are not part of the generated online-ordering-service SDK yet, so
 * the types below are hand-written to match the backend contract. Mirrors
 * the `apiFetch` usage pattern in `services.ts` (e.g. `orderApi`).
 */
import { apiFetch, buildUrl, getToken } from "./http";
import { readParticipant } from "@/lib/participant-token";

export type ReviewSentiment = "positive" | "neutral" | "negative";

export interface ReviewItemRating {
  itemId: string;
  itemName: string;
  rating: number;
  comment?: string;
}

export interface ReviewSubmitPayload {
  rating: number;
  freeText?: string;
  itemRatings?: ReviewItemRating[];
  quickIssues?: string[];
  source?: "in_app" | "qr_card" | "email_link" | "whatsapp_inline";
}

export interface ApologyOffer {
  code: string;
  promotionId?: string;
  expiresAt?: string;
}

export interface Review {
  id: string;
  orderId: string;
  branchId: string;
  rating: number;
  sentiment: ReviewSentiment;
  freeText?: string;
  itemRatings?: ReviewItemRating[];
  quickIssues?: string[];
  status: string;
  reviewerName?: string;
  createdAt?: string;
  apologyOffer?: ApologyOffer | null;
  googleReviewUrl?: string | null;
}

export interface ReviewOrderContext {
  branchId: string;
  branchName?: string;
  orderNumber: string;
  totalAmount: number;
  completedAt?: string;
  items: { itemId: string; name: string }[];
}

export interface ReviewEligibility {
  eligible: boolean;
  reason?: string;
  existingReview?: Review | null;
  orderContext?: ReviewOrderContext | null;
}

/**
 * Public (PII-safe) review surfaces — M4. Backed by the BFF's anonymous
 * `/v1/online-ordering/...reviews...` routes, which are API-key only (no
 * diner JWT required). These never include customerId/internalNote/orderId.
 */
export interface PublicReviewReply {
  text: string;
  repliedAt?: string;
}

export interface PublicReview {
  reviewerName: string;
  rating: number;
  freeText?: string;
  itemRatings?: ReviewItemRating[];
  createdAt?: string;
  operatorReply?: PublicReviewReply | null;
}

export interface BranchReviewSummary {
  branchId: string;
  avgRating: number;
  count: number;
}

export interface ItemReviewSummary {
  itemId: string;
  avgRating: number;
  count: number;
}

export interface ItemReviewSummaryBatch {
  summaries: Record<string, ItemReviewSummary>;
}

export interface PublicReviewPage {
  items: PublicReview[];
  nextPage: number | null;
  total: number;
}

/**
 * Attached to review calls for forward-compatibility only. The BFF review
 * routes (online_ordering_service/app/routers/reviews.py) require
 * `Depends(get_current_user)` — a customer JWT — and resolve `customerId`
 * to ask customer_service whether THAT CUSTOMER may review; a participant
 * token is never consulted. Per the reviews spec
 * (docs/superpowers/specs/2026-06-27-reviews-and-feedback-block7-design.md
 * §38) reviews are customer-attributed, so group-cart/dine-in guests with
 * no customerId can never be eligible today. We still send the header (it's
 * harmless and costs nothing) in case the backend ever grows guest-review
 * support, but callers MUST gate on `canAttemptReview()` first — see there.
 */
function participantHeaders(): Record<string, string> {
  const stored = readParticipant();
  const headers: Record<string, string> = {};
  if (stored?.participantToken) {
    headers["X-Participant-Token"] = `Bearer ${stored.participantToken}`;
  }
  return headers;
}

/**
 * Whether this session has any hope of a review-eligibility call succeeding.
 * The BFF eligibility/submit routes require a customer JWT (see
 * `participantHeaders` above), so a session with no customer access token —
 * an anonymous dine-in payer or unauthenticated group-cart guest — is
 * guaranteed a 401. Call sites should skip the request entirely rather than
 * fire it and swallow the 401.
 */
export function canAttemptReview(): boolean {
  return !!getToken();
}

export const reviewApi = {
  /** GET /v1/online-ordering/orders/{orderId}/review/eligibility */
  getEligibility(orderId: string): Promise<ReviewEligibility> {
    return apiFetch<ReviewEligibility>(
      `/v1/online-ordering/orders/${encodeURIComponent(orderId)}/review/eligibility`,
      { headers: participantHeaders() },
    );
  },

  /** POST /v1/online-ordering/orders/{orderId}/review */
  submit(orderId: string, payload: ReviewSubmitPayload): Promise<Review> {
    return apiFetch<Review>(
      `/v1/online-ordering/orders/${encodeURIComponent(orderId)}/review`,
      { method: "POST", body: payload, headers: participantHeaders() },
    );
  },

  /**
   * GET /v1/online-ordering/branches/{branchId}/reviews/summary
   * Anonymous public read — API-key only, no diner session needed.
   */
  getBranchReviewsSummary(branchId: string): Promise<BranchReviewSummary> {
    return apiFetch<BranchReviewSummary>(
      `/v1/online-ordering/branches/${encodeURIComponent(branchId)}/reviews/summary`,
      { skipAuth: true },
    );
  },

  /** GET /v1/online-ordering/items/{itemId}/reviews/summary */
  getItemReviewsSummary(itemId: string): Promise<ItemReviewSummary> {
    return apiFetch<ItemReviewSummary>(
      `/v1/online-ordering/items/${encodeURIComponent(itemId)}/reviews/summary`,
      { skipAuth: true },
    );
  },

  /** GET /v1/online-ordering/items/reviews/summary?ids=a,b,c */
  getItemsReviewsSummary(ids: string[]): Promise<ItemReviewSummaryBatch> {
    const url = buildUrl("/v1/online-ordering/items/reviews/summary", {
      ids: ids.join(","),
    });
    return apiFetch<ItemReviewSummaryBatch>(url, { skipAuth: true });
  },

  /** GET /v1/online-ordering/items/{itemId}/reviews?page= */
  getItemPublicReviews(itemId: string, page = 1): Promise<PublicReviewPage> {
    const url = buildUrl(
      `/v1/online-ordering/items/${encodeURIComponent(itemId)}/reviews`,
      { page },
    );
    return apiFetch<PublicReviewPage>(url, { skipAuth: true });
  },
};
